import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
// import { cn } from "@/lib/utils";
import { 
  Pencil, 
  Trash2,
  CheckCircle,
  XCircle,
  Calendar,
  RefreshCcw,
  CalendarClock
} from 'lucide-react';
import { AnnouncementRule, deleteRule } from "../../services/announcementRuleService";
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

interface AnnouncementRulesListProps {
  rules: AnnouncementRule[];
  onEdit: (rule: AnnouncementRule) => void;
  onRefresh: () => void;
}

const AnnouncementRulesList = ({ rules, onEdit, onRefresh }: AnnouncementRulesListProps) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [ruleToDelete, setRuleToDelete] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const formatDate = (date: string | Date) => {
    return format(new Date(date), 'dd MMMM yyyy', { locale: tr });
  };

  const formatTime = (time: string | undefined) => {
    if (!time) return '';
    return time;
  };

  const getRuleTypeIcon = (type: string) => {
    switch (type) {
      case 'date_range':
        return <Calendar size={16} />;
      case 'weekly_recurring':
        return <RefreshCcw size={16} />;
      case 'annual_recurring':
        return <CalendarClock size={16} />;
      default:
        return null;
    }
  };

  const getRuleTypeText = (rule: AnnouncementRule) => {
    switch (rule.type) {
      case 'date_range':
        return `${formatDate(rule.startDate)} - ${rule.endDate ? formatDate(rule.endDate) : 'Belirsiz'}`;
      case 'weekly_recurring':
        const days = rule.daysOfWeek?.map((day: number) => {
          const dayNames = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
          return dayNames[day];
        }).join(', ');
        return `Her hafta: ${days || ''}`;
      case 'annual_recurring':
        const months = [
          'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 
          'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
        ];
        return `Her yıl: ${rule.monthDay || ''} ${rule.month !== undefined ? months[rule.month] : ''}`;
      default:
        return '';
    }
  };

  const handleDeleteClick = (id: string) => {
    setRuleToDelete(id);
    setDeleteDialogOpen(true);
    setDeleteError(null);
  };

  const handleDeleteConfirm = async () => {
    if (!ruleToDelete) return;
    
    try {
      await deleteRule(ruleToDelete);
      setDeleteDialogOpen(false);
      onRefresh();
    } catch (error) {
      console.error('Rule deletion error:', error);
      setDeleteError('Kural silinirken bir hata oluştu.');
    }
  };

  return (
    <div className="w-full">
      {rules.length === 0 ? (
        <p className="text-center my-4 text-muted-foreground">
          Henüz tanımlanmış duyuru engelleme kuralı bulunmuyor.
        </p>
      ) : (
        <Card className="mt-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Durum</TableHead>
                <TableHead>Kural Adı</TableHead>
                <TableHead>Tür</TableHead>
                <TableHead>Süre/Tekrarlama</TableHead>
                <TableHead>Saat Aralığı</TableHead>
                <TableHead>Öncelik</TableHead>
                <TableHead>İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rules.map((rule) => (
                <TableRow key={rule._id}>
                  <TableCell>
                    {rule.isActive ? (
                      <Badge 
                        variant="outline" 
                        className="flex items-center gap-1 bg-green-50 text-green-700 border-green-200"
                      >
                        <CheckCircle size={12} />
                        Aktif
                      </Badge>
                    ) : (
                      <Badge 
                        variant="outline" 
                        className="flex items-center gap-1 bg-gray-50 text-gray-500 border-gray-200"
                      >
                        <XCircle size={12} />
                        Pasif
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getRuleTypeIcon(rule.type)}
                      <span className="text-sm font-medium">{rule.name}</span>
                    </div>
                    {rule.description && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {rule.description}
                      </p>
                    )}
                  </TableCell>
                  <TableCell>
                    {rule.type === 'date_range' && 'Tarih Aralığı'}
                    {rule.type === 'weekly_recurring' && 'Haftalık Tekrar'}
                    {rule.type === 'annual_recurring' && 'Yıllık Tekrar'}
                  </TableCell>
                  <TableCell>{getRuleTypeText(rule)}</TableCell>
                  <TableCell>
                    {rule.startTime && rule.endTime 
                      ? `${formatTime(rule.startTime)} - ${formatTime(rule.endTime)}` 
                      : 'Tüm gün'}
                  </TableCell>
                  <TableCell>{rule.priority || 'Normal'}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 text-blue-600"
                        onClick={() => onEdit(rule)}
                      >
                        <Pencil size={16} />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="h-8 w-8 text-red-600"
                        onClick={() => handleDeleteClick(rule._id as string)}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog 
        open={deleteDialogOpen} 
        onOpenChange={() => setDeleteDialogOpen(!deleteDialogOpen)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kuralı Sil</DialogTitle>
            <DialogDescription>
              Bu duyuru engelleme kuralını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
            </DialogDescription>
          </DialogHeader>
          
          {deleteError && (
            <div className="mt-4">
              <p className="text-sm text-red-600">{deleteError}</p>
            </div>
          )}
          
          <DialogFooter className="mt-4">
            <Button 
              variant="outline" 
              onClick={() => setDeleteDialogOpen(false)}
            >
              İptal
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteConfirm}
            >
              Sil
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AnnouncementRulesList;
